// When a running mic needs fixing, and how: a pure state machine (no Web Audio), so it can be
// tested on its own and every host gets the same rules. MicCapture feeds it what it sees every
// WATCH_MS and does what it says.
//
// The rules, from the phone's logs (logs\app.log, `mic_silent`, `mic_track_muted`):
// - A track that is live and unmuted, on a running context, that gives no frames is dead
//   somewhere we can't see (the context, the source, the track): always a FULL rebuild, a new
//   context and a new getUserMedia track. Reusing the context was what kept it deaf.
// - The track's sample rate changed (Bluetooth HFP at 16 or 24 kHz came or went), or the route
//   did (another input): rebuild, before the old context goes deaf.
// - A context whose clock froze while "running": rebuild.
// - A muted track (iOS: a brief interruption, Siri, another app): iOS usually unmutes it by
//   itself, so wait MUTE_GRACE_MS first, then fetch a fresh one.
// - The mic is on but its track isn't bound to the context (the context was swapped mid-rebuild,
//   or a bind failed): after UNBOUND_MS, rebuild; it never sits there silent with nobody told.
// - A context iOS "interrupted" (a call) or a hidden page: wait; ./phone/hold.ts handles calls.
// A rebuild that would need a new permission prompt without a gesture (a home-screen web app,
// or the site set to "Ask") isn't tried: the capture reports it (needGesture) and the page asks
// for the one tap at once.
// Rebuilds back off (REBUILD_BACKOFF_MS) and are bounded (MAX_AUTO_REBUILDS): after that the
// capture gives up once ("gesture") and the page asks for a single tap, which starts afresh. A
// mic that then runs healthy for HEALTHY_MS earns its attempts back.

export type RebuildReason =
  | "silent_live" // running context, live unmuted track, no frames
  | "track_ended"
  | "track_muted" // still muted after the grace period
  | "ctx_closed"
  | "ctx_suspended" // won't resume
  | "ctx_frozen" // "running" with a clock that doesn't move
  | "rate_change" // the track's sample rate changed since it was bound
  | "route_change" // the input device changed
  | "rate_mismatch" // the context runs at another rate than the track: rebound once after getUserMedia
  | "unbound" // the mic is on but its track isn't on the context (a swap or a bind that failed)
  | "manual"; // recover() called by the host

export interface MicObservation {
  now: number;
  /** The page is visible (nothing is recovered in the background). */
  visible: boolean;
  /** A start or rebuild is under way. */
  starting: boolean;
  ctxState: "running" | "suspended" | "interrupted" | "closed" | "none" | string;
  /** "running" but its clock doesn't move (AudioHub.frozen). */
  ctxFrozen: boolean;
  trackState: "live" | "ended" | "none" | string;
  trackMuted: boolean;
  /** When the last frame arrived (0: none since the track was bound). */
  lastFrameAt: number;
  /** The track is bound to the context (a source and the capture node), and since when. */
  bound: boolean;
  boundAt: number;
  /** The track's sample rate when it was bound, and now (null: the browser doesn't say). */
  boundTrackRate: number | null;
  trackRate: number | null;
  /** The input device changed since the track was bound (devicechange + a different track). */
  routeChanged: boolean;
}

export type MicDecision =
  | { kind: "none" }
  | { kind: "wait"; why: "hidden" | "interrupted" | "muted" | "backoff" | "starting" | "resuming" | "binding" }
  | { kind: "resume" } // nudge ctx.resume()
  | { kind: "rebuild"; reason: RebuildReason; attempt: number }
  | { kind: "gesture"; reason: RebuildReason }; // out of automatic attempts: ask for a tap (once)

/** How often the capture looks. */
export const WATCH_MS = 500;
/** No frames for this long on a live track is a stall. */
export const STALL_MS = 2500;
/** A muted track gets this long to unmute by itself before a fresh one is fetched. */
export const MUTE_GRACE_MS = 2000;
/** A mic that is on but not bound to the context gets this long (a bind in flight) before a rebuild. */
export const UNBOUND_MS = 1500;
/** A suspended context gets this long to resume before it is rebuilt. */
export const SUSPENDED_MS = 3000;
/** Waits before the 1st, 2nd, 3rd automatic rebuild in a row. */
export const REBUILD_BACKOFF_MS = [0, 1500, 4000];
export const MAX_AUTO_REBUILDS = REBUILD_BACKOFF_MS.length;
/** Healthy frames for this long after a rebuild earn the attempts back. */
export const HEALTHY_MS = 15_000;

export class MicRecovery {
  /** Automatic rebuilds in a row (reset by a healthy stretch or a tap). */
  attempts = 0;
  /** Out of attempts: the page asked for a tap; nothing more until reset() (or, for a muted
   * track, until it unmutes). */
  gaveUp = false;
  /** Why it gave up. */
  private gaveUpFor: RebuildReason | null = null;
  private lastRebuildAt = -Infinity;
  private mutedSince: number | null = null;
  private suspendedSince: number | null = null;
  private healthySince: number | null = null;
  private unboundSince: number | null = null;

  /** What to do now. Call every WATCH_MS while the mic is meant to be on. */
  decide(o: MicObservation): MicDecision {
    // Gave up only because iOS held the track (muted: an app switch of a few seconds, a call),
    // and it gave it back: a new episode, with its attempts back, so the mic comes back with no
    // tap where it can (the context resumed, or rebuilt from a clone of the live track). Before,
    // the give-up stuck: past MUTE_GRACE_MS away, the page always wanted a tap.
    if (this.gaveUp && this.gaveUpFor === "track_muted" && o.visible && !o.trackMuted && o.trackState === "live") this.reset();
    if (this.gaveUp) return { kind: "none" };
    if (o.starting) return { kind: "wait", why: "starting" };
    if (!o.visible) {
      this.mutedSince = this.suspendedSince = null;
      return { kind: "wait", why: "hidden" };
    }
    if (o.ctxState === "interrupted") return { kind: "wait", why: "interrupted" };
    if (o.trackState === "ended" || o.trackState === "none") return this.rebuild(o.now, "track_ended");
    if (o.trackMuted) {
      this.healthySince = null;
      this.mutedSince ??= o.now;
      if (o.now - this.mutedSince < MUTE_GRACE_MS) return { kind: "wait", why: "muted" };
      return this.rebuild(o.now, "track_muted");
    }
    this.mutedSince = null;
    if (o.ctxState === "closed" || o.ctxState === "none") return this.rebuild(o.now, "ctx_closed");
    if (o.ctxState !== "running") {
      this.suspendedSince ??= o.now;
      if (o.now - this.suspendedSince < SUSPENDED_MS) return { kind: "resume" };
      return this.rebuild(o.now, "ctx_suspended");
    }
    this.suspendedSince = null;
    if (!o.bound) {
      this.unboundSince ??= o.now;
      if (o.now - this.unboundSince < UNBOUND_MS) return { kind: "wait", why: "binding" };
      return this.rebuild(o.now, "unbound");
    }
    this.unboundSince = null;
    if (o.boundTrackRate && o.trackRate && o.trackRate !== o.boundTrackRate) return this.rebuild(o.now, "rate_change");
    if (o.routeChanged) return this.rebuild(o.now, "route_change");
    if (o.ctxFrozen) return this.rebuild(o.now, "ctx_frozen");
    const since = o.lastFrameAt || o.boundAt;
    if (o.now - since > STALL_MS) return this.rebuild(o.now, "silent_live");
    // Healthy: frames are arriving.
    if (o.lastFrameAt) {
      this.healthySince ??= o.now;
      if (this.attempts && o.now - this.healthySince >= HEALTHY_MS) this.attempts = 0;
    }
    return { kind: "none" };
  }

  /** The host asked for a rebuild (recover()): counted like any other, no backoff wait. */
  manual(now: number): MicDecision {
    if (this.attempts >= MAX_AUTO_REBUILDS) return this.giveUp("manual");
    return this.take(now, "manual");
  }

  /**
   * The rebuild can't happen without a gesture (it would prompt for the mic): give up now and
   * ask for the tap, rather than spend attempts that can't work.
   */
  needGesture(reason: RebuildReason): MicDecision {
    return this.giveUp(reason);
  }

  /** A tap (or a new start): a fresh episode, attempts back. */
  reset(): void {
    this.attempts = 0;
    this.gaveUp = false;
    this.gaveUpFor = null;
    this.lastRebuildAt = -Infinity;
    this.mutedSince = this.suspendedSince = this.healthySince = this.unboundSince = null;
  }

  private rebuild(now: number, reason: RebuildReason): MicDecision {
    this.healthySince = null;
    if (this.attempts >= MAX_AUTO_REBUILDS) return this.giveUp(reason);
    if (now - this.lastRebuildAt < REBUILD_BACKOFF_MS[this.attempts]) return { kind: "wait", why: "backoff" };
    return this.take(now, reason);
  }

  private take(now: number, reason: RebuildReason): MicDecision {
    this.attempts++;
    this.lastRebuildAt = now;
    this.mutedSince = this.suspendedSince = this.unboundSince = null;
    return { kind: "rebuild", reason, attempt: this.attempts };
  }

  private giveUp(reason: RebuildReason): MicDecision {
    this.gaveUp = true;
    this.gaveUpFor = reason;
    return { kind: "gesture", reason };
  }
}
